import { Button, DestructiveConfirmDialog, TimelineInteractiveCard } from "@tellescope/ui";
import { TimelineCardBaseProps } from "../types";
import { Copy, Eye, Pin, Send, Smartphone, Tag, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatPhone } from "@tellescope/utils";
import { useAuth, usePermission, useTellescopeInfiniteQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import { useState } from "react";
import { SMSContextCard } from "../../../../../tickets/context-cards";
import { Link } from "../../../../../Link";
import { toast } from "sonner";
import { AddTagContent } from "../AddTagContent";

export interface SMSMessageTimelineCardProps extends TimelineCardBaseProps<"sms_messages"> {}

export const SMSMessageTimelineCard = ({ item: message, className }: SMSMessageTimelineCardProps) => {
  const { t } = useTranslation("timeline");
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { access: canDelete } = usePermission({
    model: "sms_messages",
    action: "delete",
  });
  const { access: canReply } = usePermission({
    model: "sms_messages",
    action: "create",
  });
  const { access: canUpdate } = usePermission({
    model: "sms_messages",
    action: "update",
  });

  const deleteSMSMutation = useTellescopeMutation({
    model: "sms_messages",
    method: "deleteOne",
  });

  const updateSMSMutation = useTellescopeMutation({
    model: "sms_messages",
    method: "updateOne",
  });

  const threadIdQuery = useTellescopeInfiniteQuery({
    model: "inbox_threads",
    method: "load_threads",
    request: {
      autobuild: true,
      limit: 1,
      mdbFilter: {
        type: "SMS",
        enduserPhoneNumber: message.enduserPhoneNumber,
        phoneNumber: message.phoneNumber,
      },
    },
    getNextPageParam: (lastPage) => lastPage.threads[lastPage.threads.length - 1]?.timestamp,
    initialPageParam: undefined,
    paginateRequest: (lastTimestamp, request) => ({
      ...request,
      lastTimestamp,
    }),
    select: (data) => data.pages.flatMap((page) => page.threads).at(-1)?.id,
  });

  return (
    <>
      <TimelineInteractiveCard
        icon={Smartphone}
        title={message.enduserPhoneNumber ? formatPhone(message.enduserPhoneNumber) : t("Unknown Phone Number")}
        timestamp={message.timestamp ?? message.createdAt}
        isPinned={!!message.pinnedAt}
        isUnread={currentUser ? !message.readBy?.[currentUser.id] : false}
        preview={{
          content: message.templatedMessage ?? message.message,
        }}
        tags={message.tags}
        primaryAction={
          isOpen
            ? [
                {
                  label: t("Reply"),
                  content:
                    threadIdQuery.data && canReply ? (
                      <Link to={`/messaging/inbox/sms/${threadIdQuery.data}`}>
                        <Button size="sm" className="max-h-[30px]">
                          <Send />
                          {t("Reply")}
                        </Button>
                      </Link>
                    ) : (
                      <div />
                    ),
                },
                {
                  label: t("Collapse"),
                  onClick: () => {
                    setIsOpen(false);
                  },
                },
              ]
            : undefined
        }
        actions={[
          [
            {
              icon: Eye,
              label: t("View Messages"),
              onClick: () => {
                setIsOpen(true);
                if (currentUser) {
                  updateSMSMutation.mutateAsync({
                    id: message.id,
                    readBy: {
                      [currentUser.id]: new Date(),
                    },
                  });
                }
              },
            },
            {
              icon: Trash2,
              label: t("Delete Message"),
              onClick: () => {
                setIsDeleting(true);
              },
              hidden: !canDelete,
            },
          ],
          [
            {
              icon: Copy,
              label: t("Copy ID"),
              onClick: async () => {
                await navigator.clipboard.writeText(message.id);
                toast(t("Message ID copied"), {
                  description: t("{{messageId}} has been copied to clipboard", { messageId: message.id }),
                });
              },
            },
          ],
          [
            {
              icon: Tag,
              label: t("Add Tag"),
              submenu: (
                <AddTagContent
                  currentTags={message.tags ?? []}
                  onTagClick={async (tag) => {
                    if (!message.tags?.includes(tag)) {
                      await updateSMSMutation.mutateAsync(
                        {
                          id: message.id,
                          tags: [...(message.tags ?? []), tag],
                        },
                        {
                          onError: () => {
                            toast.error(t("Error tagging. Please try again."));
                          },
                        }
                      );
                    } else {
                      await updateSMSMutation.mutateAsync(
                        {
                          id: message.id,
                          tags: message.tags?.filter((name) => name !== tag) ?? [],
                          _options: {
                            replaceObjectFields: true,
                          },
                        },
                        {
                          onError: () => {
                            toast.error(t("Error removing tag. Please try again."));
                          },
                        }
                      );
                    }
                  }}
                  model="sms_messages"
                />
              ),
              hidden: !canUpdate,
            },
            {
              icon: Pin,
              fillIcon: !!message.pinnedAt,
              label: message.pinnedAt ? t("Remove Pin") : t("Pin"),
              onClick: () => {
                updateSMSMutation.mutateAsync({
                  id: message.id,
                  pinnedAt: message.pinnedAt ? "" : new Date(),
                });
              },
              hidden: !canUpdate,
            },
          ],
        ]}
        className={className}
      >
        {!!isOpen && <SMSContextCard smsId={message.id} />}
      </TimelineInteractiveCard>
      <DestructiveConfirmDialog
        onConfirm={() => {
          deleteSMSMutation.mutateAsync(message.id);
        }}
        onCancel={() => setIsDeleting(false)}
        onOpenChange={setIsDeleting}
        open={isDeleting}
        description={t("This will permanently delete the message. This action cannot be undone.")}
      />
    </>
  );
};
